import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { isNil, get, noop } from 'lodash';

import container from 'src/ServiceContainer';
import type { AppThunkDispatch } from 'src/store.types';
import type { AppState } from 'src/reducers';
import { makeScopeAndFilterSensitive } from 'src/components/higherOrder/ScopeAndFilterSensitive';
import { makePopoverSensitive } from 'src/components/AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import { ASSORTMENT, ASSORTMENT_BUILD_FILTER_ALL_WARNING } from 'src/utils/Domain/Constants';
import { isViewDefnLoaded, TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { TreePivot } from 'src/worker/pivotWorker.types';

import Subheader from 'src/components/Subheader/Subheader.container';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import { getHeaderTextFromState } from 'src/components/ExtendedDataGrid/ExtendedDataGrid';
import { getLocalConfig } from 'src/components/Subheader/Favorites/Favorites.utils';
import { FavoriteListItemStorage, FavoriteResponseItem } from 'src/components/Subheader/Favorites/Favorites.types';
import {
  setSelectedId,
  receiveTenantConfig as receiveStyleTenantConfig,
  requestTenantConfig as requestStyleTenantConfig,
  updateTenantConfig,
} from 'src/pages/AssortmentBuild/OvertimeView/StyleByLevel/StyleByLevelOvertime.slice';
import { NestedStyleOvertimeViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { OvertimeView } from 'src/pages/AssortmentBuild/OvertimeView/OvertimeView';
import type { ViewConfiguratorModalProps } from 'src/components/ViewConfiguratorModal/ViewConfiguratorModal.types';
import { WorklistNestedStyleOvertimeOwnProps } from 'configuration-schemas/ownProps';
import { zNestedStyleOvertimeViewDefn } from 'configuration-schemas/viewdefns/viewdefn';

export function mapStateToPropsStyleOvertime(state: AppState, ownProps: WorklistNestedStyleOvertimeOwnProps) {
  const customHeader = getHeaderTextFromState(state, true);
  const viewState = state.pages.hindsighting.styleByLevelOvertime;
  const {
    viewDataState,
    viewDefnState,
    data,
    companionData,
    viewDefn,
    unmodifiedViewDefn,
    companionDefn,
    selectedCompanionId,
    configureSelections,
    defaultConfigureSelections,
  } = viewState;
  const configLoaded = isViewDefnLoaded(viewDefnState);
  const dataLoaded = isDataLoaded(viewDataState);

  return {
    ...ownProps,
    configLoaded,
    dataLoaded,
    customHeader,
    subheaderState: state.subheader,
    data,
    companionData,
    viewDefn,
    companionDefn,
    unmodifiedViewDefn,
    selectedCompanionId,
    configureSelections,
    defaultConfigureSelections,
    aggBys: ['style', 'stylecolor'],
    viewDataState,
  };
}

export function dispatchToPropsStyleOvertime(
  dispatch: AppThunkDispatch,
  ownProps: WorklistNestedStyleOvertimeOwnProps,
) {
  const { componentProps, topMembers } = ownProps;
  const viewDefns = componentProps.defns.view;
  const dispatchHandlers = {
    async onShowView() {
      dispatch(requestStyleTenantConfig());
      const configResponse =
        await container.tenantConfigClient.getTenantViewDefnsWithFavorites<NestedStyleOvertimeViewDefn>({
          defnIds: viewDefns,
          appName: ASSORTMENT,
          validationSchemas: [zNestedStyleOvertimeViewDefn],
        });
      const unmodifiedViewDefn = configResponse[0];
      const favoritesList = (configResponse as any)[viewDefns.length];

      const localConfig: FavoriteListItemStorage | undefined = getLocalConfig(
        viewDefns[0],
        favoritesList as unknown as FavoriteResponseItem[],
        dispatch,
        unmodifiedViewDefn as unknown as TenantConfigViewData,
      );
      const configWithFavorites = isNil(localConfig)
        ? configResponse[0]
        : (localConfig.config as unknown as NestedStyleOvertimeViewDefn);

      dispatch(
        receiveStyleTenantConfig({
          viewDefn: configWithFavorites,
          unmodifiedViewDefn,
        }),
      );
      // calling this triggers the epic to fetchOvertimeData, bypassing the epic was causing extra calls to be made
      dispatch(setSelectedId(topMembers));
    },
    onRefresh(_id: string) {
      // calling this triggers the epic to fetchOvertimeData
      dispatch(setSelectedId(topMembers));
    },
    onConfigUpdate(config: NestedStyleOvertimeViewDefn) {
      dispatch(updateTenantConfig(config));
    },
    updateCompanionSortField() {
      // unused, here for types
      noop();
    },
    updateCompanionSortDirection() {
      // unused, here for types
      noop();
    },
  };

  return dispatchHandlers;
}

interface StyleOvertimeGridWithSubheader
  extends ReturnType<typeof mapStateToPropsStyleOvertime>,
    ReturnType<typeof dispatchToPropsStyleOvertime> {
  subheaderSummary?: string;
  overtimeData?: TreePivot;
}

const StyleOvertimeGridWithSubheader = ({ subheaderSummary, ...props }: StyleOvertimeGridWithSubheader) => {
  const {
    viewDefn,
    data,
    subheaderState,
    unmodifiedViewDefn,
    dataLoaded,
    aggBys,
    customHeader,
    topMembers,
    onRefresh,
    onShowView,
    viewDataState,
  } = props;

  useEffect(() => {
    onShowView();
  }, []);

  useEffect(() => {
    onRefresh('');
    // we only want to trigger this when the item changes, onRefresh is changed every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topMembers]);

  const onUpdateConfig = (config: TenantConfigViewData) => {
    if (props.onConfigUpdate) {
      props.onConfigUpdate(config as unknown as NestedStyleOvertimeViewDefn);
    }
  };

  const viewConfigurator: ViewConfiguratorModalProps = {
    viewConfig: viewDefn as unknown as TenantConfigViewData,
    unmodifiedViewDefn: unmodifiedViewDefn as unknown as TenantConfigViewData,
    updateConfig: onUpdateConfig,
  };
  const filteredViewDefnCols = viewDefn.columns?.filter((col) => isNil(col.visible) || col.visible);
  const finalViewDefn = { ...viewDefn, columns: filteredViewDefnCols };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Subheader
        title={''}
        viewConfigurator={viewConfigurator}
        showSearch={true}
        showFlowStatus={false}
        summary={subheaderSummary}
        errorCondition={ASSORTMENT_BUILD_FILTER_ALL_WARNING}
        viewDataState={viewDataState}
      />
      <div style={{ flex: 1 }}>
        <OvertimeView
          loaded={dataLoaded}
          viewConfig={finalViewDefn}
          rowHeight={get(finalViewDefn, 'main.rowHeight', 30)}
          aggBys={aggBys}
          treeData={data || []}
          search={subheaderState.search}
          hasCompanion={false}
          exportOptions={{
            fileName: 'Style Color Over Time',
            customHeader: customHeader,
          }}
        />
      </div>
    </div>
  );
};

export const TabbedStyleOvertimeGrid = connect(
  mapStateToPropsStyleOvertime,
  dispatchToPropsStyleOvertime,
  // @ts-ignore
)(makeScopeAndFilterSensitive(makePopoverSensitive(StyleOvertimeGridWithSubheader)));
